import { Check, ExternalLink, Eye, RefreshCw, Sparkles, X } from 'lucide-react'
import type { ItineraryProposal } from '../types'
import { Button } from './ui'

function proposalDayLabel(dayId: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayId)
  if (!match) return dayId
  const [, year, month, day] = match
  return new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${year}-${month}-${day}T12:00:00Z`))
}

export type MillerProposalState = 'pending' | 'applied' | 'dismissed' | 'stale'

interface MillerProposalCardProps {
  proposal: ItineraryProposal
  state?: MillerProposalState
  compact?: boolean
  canApply?: boolean
  showSources?: boolean
  error?: string
  onReview?: () => void
  onAdjust?: () => void
  onDismiss?: () => void
  onViewItinerary?: () => void
}

export function MillerProposalCard({
  proposal,
  state = 'pending',
  compact = false,
  canApply = true,
  showSources = true,
  error,
  onReview,
  onAdjust,
  onDismiss,
  onViewItinerary,
}: MillerProposalCardProps) {
  if (state === 'dismissed') return null

  const applied = state === 'applied'
  const stale = state === 'stale'
  const rewrite = proposal.operations.find((operation) => operation.type === 'replace_days')
  const rewriteDays = rewrite?.days ?? []
  const eyebrow = applied
    ? 'Change applied'
    : stale
      ? 'Plan needs a refresh'
      : rewriteDays.length
        ? 'Multi-day rewrite ready to compare'
        : 'Miller Time found the best fit'

  return (
    <article className={`miller-proposal${compact ? ' compact' : ''}${applied ? ' applied' : ''}${stale ? ' stale' : ''}`} aria-live={compact ? undefined : 'polite'}>
      <img src="/brand/mt-travel-logo-320.jpg" alt="" />
      <div className="proposal-copy">
        <span>{eyebrow}</span>
        <h3>{proposal.summary}</h3>
        <p>{proposal.rationale}</p>
        <div className="proposal-impact">
          <strong>{rewriteDays.length ? `${rewriteDays.length}-day rewrite` : `${proposal.operations.length} coordinated edit${proposal.operations.length === 1 ? '' : 's'}`}</strong>
          <span>Original and new plans open side by side before anything changes.</span>
        </div>
        {rewriteDays.length ? <details className="proposal-day-preview"><summary>Days in this rewrite</summary><ul>{rewriteDays.map((day) => <li key={day.dayId}><strong>{proposalDayLabel(day.dayId)}</strong><span>{day.title} · Stay: {day.location}</span></li>)}</ul></details> : null}
        {proposal.warnings.length ? <ul>{proposal.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : null}
        {showSources && proposal.sources.length ? (
          <div className="proposal-sources">
            {proposal.sources.map((source) => <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>{source.title}<ExternalLink /></a>)}
          </div>
        ) : null}
        {error ? <div className="proposal-error" role="alert">{error}</div> : null}
        {!canApply && !applied ? <small className="proposal-viewer-note">You can review this idea, but only a trip editor can apply it.</small> : null}
      </div>
      <div className="proposal-actions">
        {applied ? (
          <>
            <span className="proposal-applied"><Check />Itinerary updated</span>
            {onReview ? <Button className="secondary" type="button" onClick={onReview}><Eye />View comparison</Button> : null}
            {onViewItinerary ? <Button className="secondary" type="button" onClick={onViewItinerary}><Eye />View itinerary</Button> : null}
          </>
        ) : stale ? (
          <>
            {onAdjust ? <Button className="secondary" type="button" onClick={onAdjust}><RefreshCw />Refresh with MT</Button> : null}
            {onDismiss ? <button className="proposal-dismiss" type="button" onClick={onDismiss}><X />Not now</button> : null}
          </>
        ) : (
          <>
            {onReview ? <Button className="success" type="button" onClick={onReview}><Eye />Review comparison</Button> : null}
            {onAdjust ? <Button className="secondary" type="button" onClick={onAdjust}><Sparkles />Tweak it</Button> : null}
            {onDismiss ? <button className="proposal-dismiss" type="button" onClick={onDismiss}><X />Not now</button> : null}
          </>
        )}
      </div>
    </article>
  )
}
